// The BPMN surface — bpmn-js, the engine behind Camunda Modeler and the VS Code
// BPMN extension, so a diagram renders here exactly as on someone's desktop.
// The parent owns the toolbar; this component exposes the instance via onReady
// and a couple of imperative helpers (zoom, lane highlight, export).
import { useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import BpmnViewer from 'bpmn-js/lib/NavigatedViewer';
import BpmnModeler from 'bpmn-js/lib/Modeler';
import 'bpmn-js/dist/assets/diagram-js.css';
import 'bpmn-js/dist/assets/bpmn-js.css';
import 'bpmn-js/dist/assets/bpmn-font/css/bpmn.css';

export interface BpmnCanvasHandle {
  zoom(delta: number): void;
  fit(): void;
  /** Highlight every element whose name matches (lane, participant or task). */
  highlightByName(name: string | null): void;
  saveXML(): Promise<string>;
}

export const BpmnCanvas = forwardRef<BpmnCanvasHandle, {
  xml: string | null;
  editable?: boolean;
  fill?: boolean;
  emptyMessage?: string;
  onDirty?: (dirty: boolean) => void;
  onError?: (message: string | null) => void;
}>(function BpmnCanvas({ xml, editable, fill, emptyMessage, onDirty, onError }, ref) {
  const containerRef = useRef<HTMLDivElement>(null);
  const instanceRef = useRef<any>(null);
  const markedRef = useRef<string[]>([]);
  const [parseError, setParseError] = useState<string | null>(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const instance = editable
      ? new BpmnModeler({ container: containerRef.current })
      : new BpmnViewer({ container: containerRef.current });
    instanceRef.current = instance;
    if (editable && onDirty) instance.on('commandStack.changed', () => onDirty(true));
    return () => { instance.destroy(); instanceRef.current = null; };
  }, [editable]);

  useEffect(() => {
    const instance = instanceRef.current;
    if (!instance || !xml?.trim()) return;
    let cancelled = false;
    instance.importXML(xml)
      .then(() => {
        if (cancelled) return;
        instance.get('canvas').zoom('fit-viewport');
        setParseError(null);
        onError?.(null);
        onDirty?.(false);
      })
      .catch((err: any) => {
        if (cancelled) return;
        const msg = err?.message || 'unreadable';
        setParseError(msg);
        onError?.(msg);
      });
    return () => { cancelled = true; };
    // re-import when the mode flips too: toggling `editable` rebuilds the
    // instance, and without this the fresh modeler/viewer never loads the XML.
  }, [xml, editable]);

  useImperativeHandle(ref, () => ({
    zoom(delta) {
      const canvas = instanceRef.current?.get('canvas');
      if (canvas) canvas.zoom(canvas.zoom() + delta);
    },
    fit() {
      instanceRef.current?.get('canvas')?.zoom('fit-viewport');
    },
    highlightByName(name) {
      const instance = instanceRef.current;
      if (!instance) return;
      const canvas = instance.get('canvas');
      for (const id of markedRef.current) { try { canvas.removeMarker(id, 'cde-highlight'); } catch {} }
      markedRef.current = [];
      if (!name) return;
      const registry = instance.get('elementRegistry');
      const matches = registry.filter((el: any) => el.businessObject?.name === name);
      for (const el of matches) {
        try { canvas.addMarker(el.id, 'cde-highlight'); markedRef.current.push(el.id); } catch {}
      }
      if (matches[0]) canvas.scrollToElement?.(matches[0]);
    },
    async saveXML() {
      const { xml: out } = await instanceRef.current.saveXML({ format: true });
      return out;
    },
  }), []);

  if (!xml?.trim()) {
    return <div className={`bpmn-canvas${fill ? ' bpmn-canvas--fill' : ''}`}><div className="bpmn-empty">{emptyMessage ?? '—'}</div></div>;
  }
  return (
    <div className={`bpmn-canvas${fill ? ' bpmn-canvas--fill' : ''}`}>
      {parseError && <div className="bpmn-empty">{parseError}</div>}
      <div ref={containerRef} style={{ position: 'absolute', inset: 0, display: parseError ? 'none' : undefined }} />
    </div>
  );
});
